/**
 * Error Logger Utility
 * 
 * Production-ready error logging system for the RealtyGenie application.
 * Provides structured error logging with severity levels, context capture,
 * and integration points for external error tracking services.
 * 
 * Features:
 * - Multiple severity levels (error, warning, info, debug)
 * - Context capture (user, location, additional data)
 * - Console logging in development
 * - Structured format for external services
 * - Error deduplication
 * - Rate limiting for repeated errors
 * 
 * @file src/utils/errorLogger.ts
 * @created October 11, 2025
 */

// ============================================================================
// Types
// ============================================================================

export type ErrorSeverity = 'error' | 'warning' | 'info' | 'debug';

export interface ErrorContext {
  /** User ID if available */
  userId?: string;
  /** User email if available */
  userEmail?: string;
  /** Agency ID if available */
  agencyId?: string;
  /** Current page/route */
  location?: string;
  /** Component where error occurred */
  component?: string;
  /** Additional context data */
  [key: string]: any;
}

export interface LoggedError {
  /** Unique error ID */
  id: string;
  /** When the error occurred */
  timestamp: Date;
  /** Severity level */
  severity: ErrorSeverity;
  /** Error message */
  message: string;
  /** Error stack trace (if available) */
  stack?: string;
  /** Error name/type */
  errorType?: string;
  /** Additional context */
  context?: ErrorContext;
  /** Number of times this error occurred */
  count?: number;
}

// ============================================================================
// Configuration
// ============================================================================

const config = {
  /** Enable console logging */
  enableConsoleLogging: process.env.NODE_ENV !== 'production',
  /** Enable external service integration (e.g., Sentry) */
  enableExternalLogging: false,
  /** Maximum number of errors to store in memory */
  maxErrorsInMemory: 100,
  /** Rate limit window in milliseconds */
  rateLimitWindow: 5000, // 5 seconds
  /** Maximum occurrences of same error in rate limit window */
  maxOccurrencesInWindow: 3,
};

// ============================================================================
// State
// ============================================================================

/** In-memory error storage for deduplication and rate limiting */
const errorCache = new Map<string, LoggedError>();

/** Recent error hashes for rate limiting */
const recentErrors = new Map<string, number[]>();

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique hash for an error based on message and stack
 */
function generateErrorHash(message: string, stack?: string): string {
  const baseString = `${message}${stack?.substring(0, 100) || ''}`;
  // Simple hash function (in production, consider using a crypto hash)
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `err_${Math.abs(hash).toString(36)}`;
}

/**
 * Check if error should be rate limited
 */
function shouldRateLimit(errorHash: string): boolean {
  const now = Date.now();
  const occurrences = recentErrors.get(errorHash) || [];
  
  // Filter out old occurrences
  const recentOccurrences = occurrences.filter(
    timestamp => now - timestamp < config.rateLimitWindow
  );
  
  if (recentOccurrences.length >= config.maxOccurrencesInWindow) {
    return true;
  }
  
  // Add current occurrence
  recentOccurrences.push(now);
  recentErrors.set(errorHash, recentOccurrences);
  
  return false;
}

/**
 * Format error for console output
 */
function formatForConsole(loggedError: LoggedError): void {
  const { severity, message, context, stack } = loggedError;
  
  const styles = {
    error: 'color: #ff4444; font-weight: bold;',
    warning: 'color: #ffaa00; font-weight: bold;',
    info: 'color: #4444ff;',
    debug: 'color: #888888;',
  };
  
  const emoji = {
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️',
    debug: '🔍',
  };
  
  console.group(
    `%c${emoji[severity]} ${severity.toUpperCase()}: ${message}`,
    styles[severity]
  );
  
  console.groupEnd();
}

/**
 * Send error to external logging service
 * Placeholder for integration with services like Sentry, LogRocket, etc.
 */
function sendToExternalService(loggedError: LoggedError): void {
  if (!config.enableExternalLogging) {
    return;
  }
  
  // TODO: Integrate with external error tracking service
  // Example: Sentry.captureException(error, { contexts: { custom: context } });
  
  // For now, just a placeholder
  if (process.env.NODE_ENV === 'development') {
  }
}

/**
 * Clean up old errors from memory
 */
function cleanupErrorCache(): void {
  if (errorCache.size <= config.maxErrorsInMemory) {
    return;
  }
  
  // Convert to array and sort by timestamp
  const errors = Array.from(errorCache.entries())
    .sort((a, b) => a[1].timestamp.getTime() - b[1].timestamp.getTime());
  
  // Remove oldest errors
  const toRemove = errors.slice(0, errors.length - config.maxErrorsInMemory);
  toRemove.forEach(([hash]) => errorCache.delete(hash));
}

// ============================================================================
// Main Logging Function
// ============================================================================

/**
 * Log an error with context
 * 
 * @param severity - Error severity level
 * @param message - Error message
 * @param error - Error object (optional)
 * @param context - Additional context (optional)
 */
export function logError(
  severity: ErrorSeverity,
  message: string,
  error?: Error | unknown,
  context?: ErrorContext
): void {
  // Extract error details
  const errorObj = error instanceof Error ? error : undefined;
  const stack = errorObj?.stack;
  const errorType = errorObj?.name;
  
  // Generate error hash for deduplication
  const errorHash = generateErrorHash(message, stack);
  
  // Check rate limiting
  if (shouldRateLimit(errorHash)) {
    // Increment count on existing error
    const existing = errorCache.get(errorHash);
    if (existing) {
      existing.count = (existing.count || 1) + 1;
    }
    return;
  }
  
  // Create logged error object
  const loggedError: LoggedError = {
    id: errorHash,
    timestamp: new Date(),
    severity,
    message,
    stack,
    errorType,
    context: {
      ...context,
      location: context?.location || window.location.pathname,
      userAgent: navigator.userAgent,
    },
    count: 1,
  };
  
  // Store in cache
  errorCache.set(errorHash, loggedError);
  cleanupErrorCache();
  
  // Console logging
  if (config.enableConsoleLogging) {
    formatForConsole(loggedError);
  }
  
  // External service logging
  if (severity === 'error' || severity === 'warning') {
    sendToExternalService(loggedError);
  }
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Log an error
 */
export function error(message: string, error?: Error | unknown, context?: ErrorContext): void {
  logError('error', message, error, context);
}

/**
 * Log a warning
 */
export function warning(message: string, context?: ErrorContext): void {
  logError('warning', message, undefined, context);
}

/**
 * Log info
 */
export function info(message: string, context?: ErrorContext): void {
  logError('info', message, undefined, context);
}

/**
 * Log debug information
 */
export function debug(message: string, context?: ErrorContext): void {
  logError('debug', message, undefined, context);
}

// ============================================================================
// Error Retrieval
// ============================================================================

/**
 * Get all logged errors
 */
export function getLoggedErrors(): LoggedError[] {
  return Array.from(errorCache.values())
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

/**
 * Get errors by severity
 */
export function getErrorsBySeverity(severity: ErrorSeverity): LoggedError[] {
  return getLoggedErrors().filter(err => err.severity === severity);
}

/**
 * Clear all logged errors
 */
export function clearErrors(): void {
  errorCache.clear();
  recentErrors.clear();
}

/**
 * Get error statistics
 */
export function getErrorStats(): {
  total: number;
  bySevert: Record<ErrorSeverity, number>;
  recentErrors: number; // Last 5 minutes
} {
  const all = getLoggedErrors();
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  
  return {
    total: all.length,
    bySevert: {
      error: all.filter(e => e.severity === 'error').length,
      warning: all.filter(e => e.severity === 'warning').length,
      info: all.filter(e => e.severity === 'info').length,
      debug: all.filter(e => e.severity === 'debug').length,
    },
    recentErrors: all.filter(e => e.timestamp.getTime() > fiveMinutesAgo).length,
  };
}

// ============================================================================
// Configuration Updates
// ============================================================================

/**
 * Update error logger configuration
 */
export function configure(options: Partial<typeof config>): void {
  Object.assign(config, options);
}

/**
 * Enable/disable external logging service
 */
export function setExternalLogging(enabled: boolean): void {
  config.enableExternalLogging = enabled;
}

// ============================================================================
// React Error Handler
// ============================================================================

/**
 * Handler for React Error Boundaries
 */
export function handleReactError(
  error: Error,
  errorInfo: { componentStack: string },
  context?: ErrorContext
): void {
  logError('error', `React Error: ${error.message}`, error, {
    ...context,
    component: errorInfo.componentStack,
    errorBoundary: true,
  });
}

// ============================================================================
// Global Error Handlers
// ============================================================================

/**
 * Setup global error handlers
 * Call this in your app initialization
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    error(
      'Unhandled Promise Rejection',
      event.reason,
      {
        promise: true,
        reason: String(event.reason),
      }
    );
  });
  
  // Handle global errors
  window.addEventListener('error', (event) => {
    error(
      'Global Error',
      event.error,
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }
    );
  });
  
  if (process.env.NODE_ENV === 'development') {
    info('Global error handlers initialized');
  }
}

// ============================================================================
// Export default
// ============================================================================

const errorLogger = {
  logError,
  error,
  warning,
  info,
  debug,
  getLoggedErrors,
  getErrorsBySeverity,
  clearErrors,
  getErrorStats,
  configure,
  setExternalLogging,
  handleReactError,
  setupGlobalErrorHandlers,
};

export default errorLogger;
